import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { useIsCustomerMode } from '@plato/app-core/hooks/use-customer-mode';
import { CurrencyOrEmpty } from '@plato/app-shared/components/currency-or-empty';
import { NumberOrEmpty } from '@plato/ui-legacy/components/typography/number-or-empty';
import { PercentageOrEmpty } from '@plato/ui-legacy/components/typography/percentage-or-empty';
import { DataList } from '@plato/ui-legacy/components/ui/data-list';
import Text from '@plato/ui-legacy/components/ui/text';
import { useBreakpoint } from '@plato/ui/util/use-breakpoint';
import * as Collapsible from '@radix-ui/react-collapsible';
import { ChevronDownIcon } from 'lucide-react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { SalesDraftOrder } from '../../../server/types/drafts-types.ts';
import { safeJoin } from '../../../utils/string-utils.ts';
import { CustomerIdName } from '../../shared/components/customer-id-name.tsx';

export type DraftSummaryProps = {
    draft: SalesDraftOrder;
};

export function DraftSummary({ draft }: DraftSummaryProps): ReactElement {
    const { t } = useTranslation('sales-draft');
    const { canReadGrossMargin, canReadTransactionStorageLocation } = useAppConfig();
    const customerMode = useIsCustomerMode();
    // Expanded by default on desktop; collapsed by default on mobile to keep the bottom compact.
    const isSm = useBreakpoint('sm');

    const normalLineItems = draft.line_items.map((x) => x.normal_line_item).filter((x) => x !== undefined);

    const totalAmount = draft.total_amount;
    const totalGrossMargin = draft.total_gross_margin;
    const grossMarginPercent = totalAmount === 0 ? undefined : totalGrossMargin / totalAmount;

    return (
        <Collapsible.Root
            defaultOpen={isSm}
            className="bg-neutral-25 flex flex-col gap-3 rounded-lg p-3.5 shrink-0 grow"
        >
            <Collapsible.Trigger className="group flex items-center justify-between gap-2">
                <Text color="neutral-800" weight="semibold">
                    {t('sales_draft__summary__overview')}
                </Text>
                <div className="flex items-center px-3">
                    <ChevronDownIcon className="h-4 w-4 text-neutral-500 transition-transform group-data-[state=closed]:-rotate-90" />
                </div>
            </Collapsible.Trigger>
            <Collapsible.Content className="flex flex-col gap-3">
                <DataList variant="stretched">
                    <DataList.Item>
                        <DataList.Label>
                            <div className="flex flex-col">
                                <Text color="neutral-500">{t('customer', { ns: 'common' })}</Text>
                                <Text color="neutral-700" weight="medium">
                                    <CustomerIdName id={draft.customer_id} name={draft.customer_name} />
                                </Text>
                            </div>
                        </DataList.Label>
                        <DataList.Value></DataList.Value>
                    </DataList.Item>
                    {canReadTransactionStorageLocation && (
                        <DataList.Item>
                            <DataList.Label>
                                <div className="flex flex-col">
                                    <Text color="neutral-500">
                                        {t('storage_location', {
                                            ns: 'common',
                                        })}
                                    </Text>
                                    <Text color="neutral-700" weight="medium">
                                        {safeJoin([draft.storage_id, draft.storage_name], ': ')}
                                    </Text>
                                </div>
                            </DataList.Label>
                            <DataList.Value />
                        </DataList.Item>
                    )}
                </DataList>
                <hr />
                <DataList variant="stretched">
                    <DataList.Item>
                        <DataList.Label>
                            <Text color="neutral-500">{t('sales_draft__summary__number_of_positions')}</Text>
                        </DataList.Label>
                        <DataList.Value>
                            <Text color="neutral-900" weight="semibold">
                                <NumberOrEmpty>{normalLineItems.length}</NumberOrEmpty>
                            </Text>
                        </DataList.Value>
                    </DataList.Item>
                    {canReadGrossMargin && !customerMode && (
                        <>
                            <DataList.Item>
                                <DataList.Label>
                                    <Text color="neutral-500">
                                        {t('sales_draft__summary__contribution_margin_percent')}
                                    </Text>
                                </DataList.Label>
                                <DataList.Value>
                                    <Text color="neutral-900" weight="semibold">
                                        <PercentageOrEmpty decimals={2}>
                                            {grossMarginPercent}
                                        </PercentageOrEmpty>
                                    </Text>
                                </DataList.Value>
                            </DataList.Item>
                            <DataList.Item>
                                <DataList.Label>
                                    <Text color="neutral-500">
                                        {t('sales_draft__summary__contribution_margin')}
                                    </Text>
                                </DataList.Label>
                                <DataList.Value>
                                    <Text color="neutral-900" weight="semibold">
                                        <CurrencyOrEmpty decimals={2}>{totalGrossMargin}</CurrencyOrEmpty>
                                    </Text>
                                </DataList.Value>
                            </DataList.Item>
                        </>
                    )}
                    <DataList.Item>
                        <DataList.Label>
                            <Text color="neutral-500">{t('sales_draft__summary__total_position')}</Text>
                        </DataList.Label>
                        <DataList.Value>
                            <Text color="neutral-900" weight="semibold">
                                <CurrencyOrEmpty decimals={2}>{totalAmount}</CurrencyOrEmpty>
                            </Text>
                        </DataList.Value>
                    </DataList.Item>
                </DataList>
                <hr />
            </Collapsible.Content>
        </Collapsible.Root>
    );
}
